/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Button, Empty, Typography, Row, Col } from 'antd';
import React, { FC, useState, Fragment } from 'react';
import { useStyles } from '~src/hooks/useStyles';
import IconButton from '~components/_common/IconButton/IconButton';

import { styleFn } from './RightDetailsBlock.style';
import { ContractorModalWindow } from '~components/popups-modals/DetailsLayouts/ContractorModalWindow';
import { useIntl } from 'react-intl';
import { ContactInformationVM, ParentInviterVM } from '~src/types/db';

// Define the full structure based on your JSON
type FullTenderDetailsItem = {
  id?: string;
  projectId?: string;
  reference?: string;
  title?: string;
  // Buyer fields
  contractor_title?: string;
  contractor_subtitle?: string;
  // Location fields
  location_1?: string;
  location_2?: string;
  // Full info objects
  contractor_information?: any;
  contacts_information?: any[];
};

type RightDetailsBlockProps = {
  isTenderPage?: boolean;
  item: FullTenderDetailsItem;
  // Primary-/Secondary Share Keys: the inviting Primary-Share-Key's contact
  // info, shown on a Secondary-Share-Key's tender notice. Same field shape
  // as `contacts_information` entries below (both are ContactInformationVM-
  // shaped), so it's rendered with the exact same renderDetailRow calls.
  primaryVendorContact?: ContactInformationVM | ParentInviterVM | null;
};

const RightDetailsBlock: FC<RightDetailsBlockProps> = ({
  isTenderPage,
  item,
  primaryVendorContact,
}) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();
  const [isModalOpen, setModalOpen] = useState(false);

  // Helper to render a label-value row
  const renderDetailRow = (
    labelId: string,
    value: string | number | null | undefined
  ) => {
    if (!value) return null;
    return (
      // Added align="top" so multi-line text aligns correctly
      <Row gutter={[8, 4]} style={{ marginBottom: '4px' }} align="top">
        <Col span={10}>
          {/* Added wordBreak to label to prevent forcing width */}
          <Typography.Text
            type="secondary"
            style={{ fontSize: '12px', wordBreak: 'break-word' }}>
            {intl.formatMessage({ id: labelId })}:
          </Typography.Text>
        </Col>
        <Col span={14}>
          <Typography.Text
            style={{ fontSize: '13px', wordBreak: 'break-word' }}>
            {value}
          </Typography.Text>
        </Col>
      </Row>
    );
  };

  const EmptyBlock: React.FC<{ onClick: () => void }> = (props) => (
    <Empty
      css={{
        padding: '5vh 0',
        margin: '0',
        border: '1px solid #E3E4E4',
        borderRadius: '5px',
      }}
      description={props.children}>
      {isTenderPage && (
        <Button
          type="primary"
          css={[styles.buttons, styles.blue]}
          onClick={props.onClick}>
          {intl.formatMessage({ id: 'add' })}
        </Button>
      )}
    </Empty>
  );

  // Check if we have any data to display
  const hasData =
    item.contractor_title ||
    item.contractor_subtitle ||
    item.location_1 ||
    item.location_2 ||
    item.contractor_information ||
    (item.contacts_information && item.contacts_information.length > 0);

  return (
    <div style={{ overflowX: 'hidden', padding: '2px 4px' }}>
      <ContractorModalWindow
        item={{
          tenderId: item.id,
          reference: item.reference,
          title: item.title,
          projectId: item.projectId,
          orderGiver: {
            contactor_title: item.contractor_title,
            contactor_subtitle: item.contractor_subtitle,
          },
          location: {
            location_1: item.location_1,
            location_2: item.location_2,
          },
          contractorCompany: item.contractor_information?.contact_key,
          contact: item.contacts_information?.map((c) => c.contact_key),
        }}
        visible={isModalOpen}
        onOk={() => setModalOpen(false)}
        onCancel={() => setModalOpen(false)}
      />

      {/*<img*/}
      {/* src="https://placehold.co/200x100?text=company_logo"*/}
      {/* alt="company logo"*/}
      {/*/>*/}

      {/* Primary-/Secondary Share Keys: rendered unconditionally (outside
          the hasData/EmptyBlock branch below) so it's never hidden behind
          the "no data yet" placeholder, and placed before "Order giver" so
          it's never visually read as part of that unrelated section. */}
      {primaryVendorContact && (
        <div style={{ marginBottom: '16px' }}>
          <Typography
            style={{
              fontSize: '16px',
              fontWeight: 'bold',
              marginBottom: '8px',
            }}>
            {intl.formatMessage({
              id: 'secondary-share-key-primary-contact-title',
            })}
          </Typography>
          {renderDetailRow(
            'detailstab-info-company-title',
            primaryVendorContact.company_title
          )}
          {renderDetailRow(
            'detailstab-info-name-1',
            primaryVendorContact.name_1
          )}
          {renderDetailRow(
            'detailstab-info-user-title-1',
            primaryVendorContact.user_title_1
          )}
          {renderDetailRow(
            'detailstab-info-email-1',
            primaryVendorContact.email_1
          )}
          {renderDetailRow(
            'detailstab-info-phone-1',
            primaryVendorContact.phone_1
          )}
          {renderDetailRow(
            'detailstab-info-company-email',
            primaryVendorContact.company_email
          )}
          <p css={styles.blocksDivider} />
        </div>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '20px',
        }}>
        <Typography style={{ fontSize: '14px' }}>
          {intl.formatMessage({ id: 'order-giver' })}
        </Typography>
        {isTenderPage && (
          <div css={styles.buttonDiv} onClick={() => setModalOpen(true)}>
            <IconButton
              icon={['far', 'pen-line']}
              containerStyle={{ marginLeft: 0, color: '#4170ff' }}
              backgroundOff
              widthTooltip="300px"
            />
          </div>
        )}
      </div>

      {!hasData ? (
        <EmptyBlock onClick={() => setModalOpen(true)}>
          {intl.formatMessage({ id: 'detailstab-tender-custom-fields' })}
        </EmptyBlock>
      ) : (
        <div>
          {/* 1. BUYER SECTION */}
          {(item.contractor_title || item.contractor_subtitle) && (
            <div style={{ marginBottom: '16px' }}>
              <Typography style={{ fontSize: '16px', fontWeight: 'bold' }}>
                {intl.formatMessage({ id: 'buyer' })}
              </Typography>
              {item.contractor_title && (
                <Typography style={{ color: 'gray', display: 'block' }}>
                  {item.contractor_title}
                </Typography>
              )}
              {item.contractor_subtitle && (
                <Typography style={{ color: 'gray', display: 'block' }}>
                  {item.contractor_subtitle}
                </Typography>
              )}
            </div>
          )}

          {/* 2. LOCATION SECTION */}
          {(item.location_1 || item.location_2) && (
            <div style={{ marginBottom: '16px' }}>
              {(item.contractor_title || item.contractor_subtitle) && (
                <p css={styles.blocksDivider} />
              )}

              <Typography style={{ fontSize: '16px', fontWeight: 'bold' }}>
                {intl.formatMessage({ id: 'location' })}
              </Typography>
              {item.location_1 && (
                <Typography style={{ color: 'gray', display: 'block' }}>
                  {item.location_1}
                </Typography>
              )}
              {item.location_2 && (
                <Typography style={{ color: 'gray', display: 'block' }}>
                  {item.location_2}
                </Typography>
              )}
            </div>
          )}

          {/* 3. CONTRACTOR COMPANY  */}
          {item.contractor_information && (
            <Fragment>
              <p css={styles.blocksDivider} />
              <div style={{ marginBottom: '16px' }}>
                <Typography
                  style={{
                    fontSize: '16px',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                  }}>
                  {intl.formatMessage({ id: 'detailstab-contactor-company' })}
                </Typography>

                {/* Company-type fields */}
                {renderDetailRow(
                  'detailstab-info-company-title',
                  item.contractor_information.company_title
                )}
                {renderDetailRow(
                  'detailstab-info-company-subtitle',
                  item.contractor_information.company_subtitle
                )}
                {renderDetailRow(
                  'detailstab-info-company-email',
                  item.contractor_information.company_email
                )}
                {renderDetailRow(
                  'detailstab-info-company-url',
                  item.contractor_information.company_url
                )}

                {/* Person-type fields */}
                {renderDetailRow(
                  'detailstab-info-name-1',
                  item.contractor_information.name_1
                )}
                {renderDetailRow(
                  'detailstab-info-name-2',
                  item.contractor_information.name_2
                )}
                {renderDetailRow(
                  'detailstab-info-title-1',
                  item.contractor_information.user_title_1
                )}
                {renderDetailRow(
                  'detailstab-info-title-2',
                  item.contractor_information.user_title_2
                )}

                {/* Contact details shared by both types */}
                {renderDetailRow(
                  'detailstab-info-email-1',
                  item.contractor_information.email_1
                )}
                {renderDetailRow(
                  'detailstab-info-email-2',
                  item.contractor_information.email_2
                )}
                {renderDetailRow(
                  'detailstab-info-phone-1',
                  item.contractor_information.phone_1
                )}
                {renderDetailRow(
                  'detailstab-info-phone-2',
                  item.contractor_information.phone_2
                )}

                {/* Address details */}
                {renderDetailRow(
                  'detailstab-info-location-1',
                  item.contractor_information.location_1
                )}
                {renderDetailRow(
                  'detailstab-info-location-2',
                  item.contractor_information.location_2
                )}
                {renderDetailRow(
                  'detailstab-info-address-1',
                  item.contractor_information.address_1
                )}
                {renderDetailRow(
                  'detailstab-info-address-2',
                  item.contractor_information.address_2
                )}
                {renderDetailRow(
                  'detailstab-info-postal-code',
                  item.contractor_information.postal_code
                )}
                {renderDetailRow(
                  'detailstab-info-city',
                  item.contractor_information.city
                )}
                {renderDetailRow(
                  'detailstab-info-region-1',
                  item.contractor_information.region_1
                )}
                {renderDetailRow(
                  'detailstab-info-region-2',
                  item.contractor_information.region_2
                )}
                {renderDetailRow(
                  'detailstab-info-country',
                  item.contractor_information.country
                )}
                {renderDetailRow(
                  'detailstab-info-timezone',
                  item.contractor_information.timezone
                )}
              </div>
            </Fragment>
          )}

          {/* 4. CONTACTS */}
          {item.contacts_information?.map((contact, index) => (
            <Fragment key={contact.contact_key || index}>
              <p css={styles.blocksDivider} />
              <div style={{ marginBottom: '16px' }}>
                <Typography
                  style={{
                    fontSize: '16px',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                  }}>
                  {intl.formatMessage({ id: 'contact' })}
                </Typography>

                {/* Contact Type */}
                {renderDetailRow('detailstab-info-title', contact.title)}

                {/* Person Info */}
                {renderDetailRow('detailstab-info-name-1', contact.name_1)}
                {renderDetailRow('detailstab-info-name-2', contact.name_2)}
                {renderDetailRow(
                  'detailstab-info-user-title-1',
                  contact.user_title_1
                )}
                {renderDetailRow(
                  'detailstab-info-user-title-2',
                  contact.user_title_2
                )}
                {renderDetailRow('detailstab-info-email-1', contact.email_1)}
                {renderDetailRow('detailstab-info-email-2', contact.email_2)}
                {renderDetailRow('detailstab-info-phone-1', contact.phone_1)}
                {renderDetailRow('detailstab-info-phone-2', contact.phone_2)}

                {/* Company Info inside Contact */}
                {renderDetailRow(
                  'detailstab-info-company-title',
                  contact.company_title
                )}
                {renderDetailRow(
                  'detailstab-info-company-subtitle',
                  contact.company_subtitle
                )}
                {renderDetailRow(
                  'detailstab-info-company-email',
                  contact.company_email
                )}
                {renderDetailRow(
                  'detailstab-info-company-url',
                  contact.company_url
                )}

                {/* Location/Address Info */}
                {renderDetailRow(
                  'detailstab-info-location-1',
                  contact.location_1
                )}
                {renderDetailRow(
                  'detailstab-info-location-2',
                  contact.location_2
                )}
                {renderDetailRow(
                  'detailstab-info-address-1',
                  contact.address_1
                )}
                {renderDetailRow(
                  'detailstab-info-address-2',
                  contact.address_2
                )}
                {renderDetailRow(
                  'detailstab-info-postal-code',
                  contact.postal_code
                )}
                {renderDetailRow('detailstab-info-city', contact.city)}
                {renderDetailRow('detailstab-info-region-1', contact.region_1)}
                {renderDetailRow('detailstab-info-region-2', contact.region_2)}
                {renderDetailRow('detailstab-info-country', contact.country)}
                {renderDetailRow('detailstab-info-timezone', contact.timezone)}
              </div>
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default RightDetailsBlock;
